import React, { useState, useMemo, useCallback } from "react";

export const BillPaperToPrint = React.forwardRef((props, ref) => {
  const { pos, order, bill, branch, user } = props;

  const [orderToDisplay, setOrderToDisplay] = useState(null);

  const formatAmount = useCallback((value) => {
    if (value === undefined || value === null || value === "-") return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return num.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  useMemo(() => {
    if (!props.order) return;

    if (order.product?.length > 0) {
      const filtered = order.product.filter(
        (prod) =>
          prod.orderID === order.id ||
          prod.orderID === order.orderID ||
          prod.orderID === order.orderId
      );
      setOrderToDisplay({ product: filtered });
    }
  }, [props.order, order]);

  const getBusinessLogo = () => {
    if (
      bill?.order?.branchPosSelling?.branchPos?.branch?.business?.image_path
    ) {
      return bill.order.branchPosSelling.branchPos.branch.business.image_path;
    }
    return "";
  };

  const renderBusinessLogo = () => {
    const logoUrl = getBusinessLogo();
    return logoUrl ? (
      <div className="flex justify-center">
        <img src={logoUrl} alt="" className="max-w-150px max-h-150px" />
      </div>
    ) : null;
  };

  const getTableDisplay = (orderData) => {
    if (!orderData) return "";

    const orderType = orderData.order_type;

    if (orderType === "DINE_IN" && orderData.branchTable?.branchTableZone) {
      return `${orderData.branchTable.branchTableZone.name}/${orderData.branchTable.name}`;
    } else if (orderType === "TAKEAWAY") {
      return "กลับบ้าน";
    } else if (orderType === "DELIVERY") {
      return "เดลิเวอรี่";
    }

    return "";
  };

  const renderInvoiceHeader = () => (
    <>
      {renderBusinessLogo()}
      <div>ใบแจ้งยอด</div>
      <div className="flex justify-between gap-1">
        <div className="whitespace-nowrap">
          วันที่ :
          {order?.start_time
            ? ` ${new Date(order.start_time).toLocaleDateString(
                "en-GB"
              )} ${new Date(order.start_time).toLocaleTimeString()}`
            : order?.operation_date
            ? ` ${new Date(order.operation_date).toLocaleDateString(
                "en-GB"
              )} ${new Date(order.operation_date).toLocaleTimeString()}`
            : ""}
        </div>
        <div className="text-right">
          POS#:{" "}
          {bill?.order?.branchPosSelling?.branchPos?.pos_id || pos?.code || "-"}
        </div>
      </div>
      <div className="flex justify-between gap-1">
        <div className="whitespace-nowrap">
          พนักงานขาย : {`${user.firstNameTH || ""} ${user.lastNameTH || ""}`}
        </div>
        <div className="text-right">โต๊ะ: {getTableDisplay(bill?.order)}</div>
      </div>
    </>
  );

  const renderProductOptions = (product, index) => {
    const standardOptions = product.optionstandard || [];
    const productOptions = product.optionproduct || [];
    const noteOptions = product.optionnote || [];

    return (
      <>
        {standardOptions.map((option, idx) => (
          <div
            key={`option-row-standard-${index}-${idx}`}
            className="flex mt-3 mb-4"
          >
            <div className="w-1/6"></div>
            <div className="w-2/4 pl-2 text-small text-xs">
              - {option.listName}{" "}
            </div>
            <div className="w-1/6 text-center text-small text-xs">
              {Number(option.listPrice || "0") > 0 && "+"}
              {Number(option.listPrice || "0") > 0
                ? formatAmount(Number(option.listPrice * product.productQty))
                : ""}
            </div>
            <div className="w-1/4 text-right text-small text-xs"></div>
          </div>
        ))}

        {productOptions.map((option, idx) => (
          <div
            key={`option-row-product-${index}-${idx}`}
            className="flex mt-3 mb-4"
          >
            <div className="w-1/6"></div>
            <div className="w-2/4 pl-2 text-small text-xs">
              - {option.listName}{" "}
              {option.optionQty > 1 && `x ${option.optionQty}`}
            </div>
            <div className="w-1/6 text-center text-small text-xs">
              {Number(option.listPrice || "0") > 0 && "+"}
              {Number(option.listPrice || "0") > 0
                ? formatAmount(Number(option.listPrice * product.productQty))
                : ""}
            </div>
            <div className="w-1/4 text-right text-small text-xs"></div>
          </div>
        ))}

        {noteOptions.map((option, idx) => (
          <div
            key={`option-row-note-${index}-${idx}`}
            className="flex mt-3 mb-4"
          >
            <div className="w-1/6"></div>
            <div className="w-2/4 pl-2 text-small text-xs">
              - {option.listName}{" "}
              {option.optionQty > 1 && `x ${option.optionQty}`}
            </div>
            <div className="w-1/6"></div>
            <div className="w-1/4"></div>
          </div>
        ))}
      </>
    );
  };

  const renderProductList = () => {
    const validProducts =
      orderToDisplay?.product?.filter((p) => p.productStatus !== 3) || [];

    console.log("validProducts : ", validProducts);

    return (
      <div>
        <div className="font-bold">
          จำนวนสินค้า:{" "}
          {validProducts.reduce(
            (sum, product) => sum + (parseInt(product.productQty) || 0),
            0
          )}
        </div>
        {validProducts.map((product, index) => (
          <div key={`product${index}`} className="mb-1">
            <div className="flex gap-1 items-start">
              <div className="w-1/6 text-center leading-[1.45]">
                {product.productQty}
              </div>
              <div className="w-2/4">
                <div className="leading-[1.45]">{product.productName}</div>
              </div>
              <div className="w-1/6 text-center leading-[1.45]">
                {formatAmount(Number(product.productPricePerPiece || 0))}
              </div>
              <div className="w-1/4 text-right leading-[1.45]">
                {formatAmount(Number(product.productPrice || 0))}
              </div>
            </div>
            {renderProductOptions(product, index)}
          </div>
        ))}
      </div>
    );
  };

  const renderPaymentSummary = () => (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between gap-1">
        <div className="whitespace-nowrap">ยอดรวมก่อนส่วนลด</div>
        <div className="text-right">
          {bill?.total_before_discount
            ? formatAmount(Number(bill.total_before_discount))
            : bill?.total
            ? formatAmount(Number(bill.total))
            : "0.00"}
        </div>
      </div>
      <div className="flex justify-between gap-1">
        <div className="whitespace-nowrap">ส่วนลดท้ายบิล</div>
        <div className="text-right">
          {bill?.discount_amount ? formatAmount(bill.discount_amount) : "0.00"}
        </div>
      </div>
      <div className="flex justify-between gap-1">
        <div className="whitespace-nowrap">ยอดขายหลังหักส่วนลด</div>
        <div className="text-right">
          {bill?.total_after_discount
            ? formatAmount(Number(bill.total_after_discount))
            : "0.00"}
        </div>
      </div>
      <div className="flex justify-between gap-1">
        <div className="whitespace-nowrap">
          Service Charge
          {bill?.service_charge_percent
            ? ` (${bill.service_charge_percent}%)`
            : branch?.financial?.service_charge &&
              branch.financial.service_charge !== "0"
            ? ` (${branch.financial.service_charge}%)`
            : ""}
        </div>
        <div className="text-right">
          {bill?.service_charge
            ? formatAmount(Number(bill.service_charge))
            : "0.00"}
        </div>
      </div>
      <div className="flex justify-between gap-1">
        <div className="whitespace-nowrap">มูลค่าสินค้าไม่รวมภาษี</div>
        <div className="text-right">
          {bill?.taxable_amount
            ? formatAmount(Number(bill.taxable_amount))
            : "0.00"}
        </div>
      </div>
      <div className="flex justify-between gap-1">
        <div className="whitespace-nowrap">ภาษีมูลค่าเพิ่ม</div>
        <div className="text-right">
          {bill?.vat ? formatAmount(Number(bill.vat)) : "0.00"}
        </div>
      </div>
      <div className="flex justify-between gap-1 text-large font-bold">
        <div className="whitespace-nowrap">ราคาสุทธิ</div>
        <div className="text-right">
          {bill?.grand_total ? formatAmount(Number(bill.grand_total)) : "0.00"}
        </div>
      </div>
      <div className="flex justify-between gap-1">
        <div className="whitespace-nowrap">จำนวนเงินปัดเศษ</div>
        <div className="text-right">
          {bill?.loss_rounded
            ? formatAmount(Number(bill.loss_rounded))
            : "0.00"}
        </div>
      </div>
      <div className="flex justify-between gap-1 text-large font-bold">
        <div className="whitespace-nowrap text-[4.7vw]">จำนวนที่ต้องชำระ</div>
        <div className="text-right text-[4.7vw]">
          {bill?.final_profit_loss_rounded
            ? formatAmount(Number(bill.final_profit_loss_rounded))
            : "0.00"}
        </div>
      </div>
    </div>
  );

  const getVatCalculator = () => {
    if (branch?.branch_info?.business?.settingVatCalculation?.code) {
      const vatCalculator =
        branch.branch_info.business.settingVatCalculation.code;
      if (vatCalculator === "INCLUDED") return "(Vat Included)";
      if (vatCalculator === "SEPARATE") return "(Vat Excluded)";
    }
    return "";
  };

  return (
    <div ref={ref} className="flex justify-center text-xs w-full">
      <div
        id="section-to-print"
        className="w-80 bg-white px-6 py-4 flex flex-col gap-2"
      >
        <div className="text-center font-semibold">{renderInvoiceHeader()}</div>

        <div className="border-dashed border-t border-black h-0 mt-3"></div>

        <div className="flex">
          <div className="w-1/6 whitespace-nowrap text-center">จำนวน</div>
          <div className="w-2/4 text-center">รายการ</div>
          <div className="w-1/6 text-center">หน่วยละ</div>
          <div className="w-1/4 text-right">รวมเงิน</div>
        </div>

        <div className="border-dashed border-t border-black h-0 mt-3"></div>

        {renderProductList()}

        <div className="border-dashed border-t border-black h-0 mt-3"></div>

        {renderPaymentSummary()}

        <div className="border-dashed border-t border-black h-0 mt-3"></div>

        <div className="text-center">
          <div>{getVatCalculator()}</div>

          <div>THANK YOU</div>
          <div>ขอบคุณที่ใช้บริการ</div>
          <div className="text-center" style={{ fontSize: "44px" }}>
            {order.remark ? `(${order.remark})` : ""}{" "}
          </div>
        </div>
      </div>
    </div>
  );
});

export default BillPaperToPrint;
